import { useState, useCallback } from 'react';
import { ApiError } from '../types/api';
import { ERROR_TEXTS, ERROR_FALLBACK } from '../constants/api';

export interface ErrorState {
  message: string;
  type: 'error' | 'warning' | 'info';
  code?: string;
  requestId?: string | null;
}

export function useErrorHandler() {
  const [error, setError] = useState<ErrorState | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleError = useCallback((error: unknown) => {
    console.error('Error handled:', error);

    if (error instanceof ApiError) {
      const apiError = error as ApiError;

      const message = ERROR_TEXTS[apiError.code] || ERROR_FALLBACK;

      setError({
        message,
        type: 'error',
        code: apiError.code,
        requestId: apiError.requestId || null,
      });

      return;
    }

    if (error instanceof Error) {
      setError({
        message: error.message || ERROR_FALLBACK,
        type: 'error',
      });
      return;
    }

    setError({
      message: ERROR_FALLBACK,
      type: 'error',
    });
  }, []);

  const handleAsync = async <T>(
    asyncFunction: () => Promise<T>
  ): Promise<T | null> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await asyncFunction();
      return result;
    } catch (err: unknown) {
      handleError(err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const showSuccess = useCallback((message: string) => {
    setError({
      message,
      type: 'info',
    });
  }, []);

  const showWarning = useCallback((message: string) => {
    setError({
      message,
      type: 'warning',
    });
  }, []);

  return {
    error,
    isLoading,
    handleAsync,
    handleError,
    clearError,
    showSuccess,
    showWarning,
  };
};
